// Layer: presentation. Inline guardrail editor (FR-OC-5).
import type { GuardrailSpec, OnFail, OnPass } from "../domain/types";

interface Props {
  value: GuardrailSpec;
  onChange: (spec: GuardrailSpec) => void;
  idPrefix: string;
}

const ON_PASS: Array<{ value: OnPass; label: string; hint: string }> = [
  {
    value: "auto_execute",
    label: "Auto-execute",
    hint: "Reversible actions below this gate may run without a human.",
  },
  {
    value: "queue",
    label: "Queue for a human",
    hint: "Everything below this gate waits for approval.",
  },
];

const ON_FAIL: Array<{ value: OnFail; label: string; hint: string }> = [
  {
    value: "block",
    label: "Block the branch",
    hint: "Nothing below this gate is produced.",
  },
  {
    value: "escalate",
    label: "Escalate",
    hint: "Actions are still produced, but always queued.",
  },
];

export function GuardrailEditor({ value, onChange, idPrefix }: Props) {
  return (
    <fieldset className="guardrail">
      <legend>Guardrail</legend>

      <label htmlFor={`${idPrefix}-predicate`}>
        Condition
        <textarea
          id={`${idPrefix}-predicate`}
          value={value.predicate}
          rows={2}
          spellCheck={false}
          placeholder="lead_hours &gt;= 4 AND severity IN ['warn', 'critical']"
          onChange={(e) => onChange({ ...value, predicate: e.target.value })}
        />
      </label>
      <p className="muted">
        Compares facts with <code>== != &gt; &lt; &gt;= &lt;=</code> and{" "}
        <code>IN</code>, combined with <code>AND OR NOT</code>. If a fact is missing the
        guardrail fails. It never passes by default.
      </p>

      <label htmlFor={`${idPrefix}-on-pass`}>
        When it passes
        <select
          id={`${idPrefix}-on-pass`}
          value={value.on_pass}
          onChange={(e) => onChange({ ...value, on_pass: e.target.value as OnPass })}
        >
          {ON_PASS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>
      <p className="muted">{ON_PASS.find((o) => o.value === value.on_pass)?.hint}</p>

      <label htmlFor={`${idPrefix}-on-fail`}>
        When it fails
        <select
          id={`${idPrefix}-on-fail`}
          value={value.on_fail}
          onChange={(e) => onChange({ ...value, on_fail: e.target.value as OnFail })}
        >
          {ON_FAIL.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>
      <p className="muted">{ON_FAIL.find((o) => o.value === value.on_fail)?.hint}</p>
    </fieldset>
  );
}
